// Layer 4 (UI) — the route's status band (locked comp `planning.html` A): one
// card, three hairline-split cells — Year 1 to date, the central Year-1
// forecast, the central Year-1 NSR — each a primary figure with its comparison
// under it (DESIGN.md § The primary figure). The cell recipe is Downtime's
// strip cell without the direction notch: these are three reads, not a chain.
// Renders PlanningBand.transform.ts; derives nothing.

import { GlassCard } from '@/design-system';
import { cn } from '@/shared/cn';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { ActorChip } from '../advisory/PipelineStrip';
import { SYN_CHIP } from '../downtime/DowntimeCard';
import type { PlanningBandCell } from './PlanningBand.transform';
import { planningProvenanceChip } from './Scene.transform';

export function PlanningBand({ cells }: { cells: readonly PlanningBandCell[] }) {
  return (
    <GlassCard
      tone="matte"
      aria-label="Year 1 status"
      data-planning-band
      className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
    >
      <div className="grid grid-cols-1 md:grid-cols-3">
        {cells.map((cell) => (
          <div
            key={cell.id}
            data-planning-band-cell={cell.id}
            className="flex min-h-[6.5rem] min-w-0 flex-col gap-1 border-b border-[var(--hairline)] bg-[var(--surface-2)] py-3 pl-4 pr-3.5 last:border-b-0 md:border-b-0 md:border-r md:last:border-r-0"
          >
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.name}</span>
              <span className="flex-1" />
              <ProvenanceChip {...planningProvenanceChip(cell.provenance)} compact />
            </span>
            <span data-planning-band-figure={cell.id} className="whitespace-nowrap text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary">
              {cell.figure}
              {cell.unit ? <span className="ml-1 text-sm font-semibold text-muted">{cell.unit}</span> : null}
            </span>
            <span data-planning-band-comparison={cell.id} className="truncate text-xs leading-snug tabular-nums text-muted">
              <span className={cn('font-semibold', cell.leadTone === 'warn' ? 'text-[var(--state-warning)]' : 'text-ink-secondary')}>{cell.lead}</span>
              {cell.rest}
            </span>
            {cell.note ? (
              <span className="flex min-w-0 items-center gap-1.5 truncate text-xs leading-snug tabular-nums text-muted">
                <span className="truncate">{cell.note}</span>
                {cell.noteChip ? <ProvenanceChip {...planningProvenanceChip(cell.noteChip)} compact /> : null}
              </span>
            ) : null}
            {cell.actor ? (
              <span className="mt-auto flex min-w-0 flex-wrap gap-1 pt-1">
                <ActorChip actor="Engine">{cell.actor}</ActorChip>
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

/** The header's period chip: `Year 1 · Mar 2026 – Feb 2027`. */
export function PlanningPeriodChip({ lead, range }: { lead: string; range: string }) {
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-2.5 py-0.5 text-xs tabular-nums text-ink-secondary">
      <span className="font-semibold text-ink-primary">{lead}</span>&nbsp;· {range}
    </span>
  );
}

export { SYN_CHIP };
